/**
 * What a die is made of, shared by every scene that draws one: the initiative
 * tray and the game board alike.
 *
 * What gives an expensive die its look cannot be painted per face:
 *
 * 1. **The chamfer as geometry.** Each face is pulled in toward its centroid
 *    and the gaps are filled with narrow strips (edges) and small caps
 *    (corners), so the bevel catches light differently on every turn.
 * 2. **The drawn numeral.** Card stock and hand-drawn numerals come from two
 *    baked images (`/game-assets/dice/{card,numerals}.webp`), composited per
 *    face into a texture atlas plus a bump map cut from the same two images.
 * 3. **Card stock has no gloss.** Paper does not mirror. What remains is a
 *    matte surface with a tooth, and the whole form comes out of the light.
 *
 * One geometry, one atlas pair and one base material per side count, built on
 * first use and kept. Scenes clone the material (a hover glow or a loser's
 * dimming must not leak onto every other die) and share the geometry.
 */

import * as THREE from 'three';

import { isDiceSkinOwned } from './diceSkinEntitlements';
import { atlasLayout, CARD, cellCenter, CHAMFER, createAtlasPainter, FACE_FILL, faceFrame, hasRoughnessMap } from './atlasPainter';
import { configureDieFoil } from './dieFoilMaterial.js';
import { cutAtlasesInWorker } from './atlasWorkerClient';

import {
  dieGeometry,
  vAdd,
  vCross,
  vDot,
  vNormalize,
  vScale,
  vSub,
} from './geometry';

import { getPref, setPref } from '../../persistentPrefs';
import { DICE_SKINS } from './diceSkins.data';
import { k } from '../../../localization/t';

const CARD_URL = '/game-assets/dice/card.webp';
const NUMERALS_URL = '/game-assets/dice/numerals.webp';

// Cutting constants live in atlasPainter.js; the geometry shares its layout.

/**
 * The dice colours a player can pick. One atlas recipe each: the ground tone
 * under the card stock, the CSS filter the stock is cut through, the tint of
 * the worn edge, and the filter on the pencilled numerals (inverted, they
 * read white on a dark body). The relief is palette-blind: paper tooth and
 * graphite lie the same in every colour. `swatch` is the button's colour.
 *
 * After the three paper colours come the **skins** (`DICE_SKINS`): dice cast
 * from a Sorcery card's artwork. A skin swaps the card stock for a baked
 * `tile` image (the art blurred and swirled past recognition, tooth already
 * in it), may bring a `height` and a `roughness` tile cut the same way for
 * relief and gloss, tints the numerals to a flat `numeralColor` instead of filtering
 * (and may thicken them with `numeralWeight`, 0..1)
 * them, and may override the paper's `material` (a resin is glossier than
 * card). The tiles are authored in the `valkenhall-assets-lab` sibling repo
 * and shipped frozen; nothing here derives anything from card art at runtime.
 * Optional `foil` settings use the cards' shared optics via dieFoilMaterial.
 * Artwork, numeral ink and chamfers have independent foil coverage.
 */
export const DICE_PALETTES = {
  brown: {
    id: 'brown', label: k('Brown'), swatch: '#8f7350',
    fill: CARD, cardFilter: 'none', wear: '58 38 20', numeralFilter: 'none',
  },
  bone: {
    id: 'bone', label: k('Bone'), swatch: '#e6dcc6',
    fill: '#e2d8c4', cardFilter: 'grayscale(0.55) brightness(1.3) contrast(0.9)', wear: '96 84 66', numeralFilter: 'none',
  },
  stone: {
    id: 'stone', label: k('Stone'), swatch: '#4a4a4e',
    fill: '#3c3c40', cardFilter: 'grayscale(1) brightness(0.42) contrast(1.1)', wear: '0 0 0', numeralFilter: 'invert(1) brightness(1.15)',
  },
  ...DICE_SKINS,
};
const PALETTE_KEY = 'dicePalette';
const DEFAULT_PALETTE = 'bone';
let paletteId = null;
/** A recipe under authoring, shown instead of the saved pick and never remembered. */
let previewPalette = null;
const paletteListeners = new Set();

export function getDicePalette() {
  if (previewPalette !== null) return previewPalette;
  paletteId ??= DICE_PALETTES[getPref(PALETTE_KEY, null)] ? getPref(PALETTE_KEY) : DEFAULT_PALETTE;
  // Entitlement is resolved per call and never written back to `paletteId`:
  // `isDiceSkinOwned` fails open while the cosmetics cache is cold, so the
  // answer can change under us. Keeping the saved pick intact means the
  // redraw that follows the cache resolving takes an unowned skin away,
  // and a player who really does own it keeps it once the catalog lands.
  const resolvedId = isDiceSkinOwned(paletteId) ? paletteId : DEFAULT_PALETTE;
  return DICE_PALETTES[resolvedId];
}

/**
 * The dice lab's hook: every die takes on this recipe until the next call.
 * `null` hands the dice back to the saved pick. The recipe may carry a
 * `tileImage` (a canvas or image) in place of a `tile` URL.
 */
export function previewDicePalette(recipe) {
  previewPalette = recipe;
  redrawDiceArtwork();
  for (const fn of paletteListeners) fn(getDicePalette());
}

/** Recolour every die there is, on the board, in the tray and in the roll-off, and remember it. */
export function setDicePalette(id) {
  if (!DICE_PALETTES[id] || !isDiceSkinOwned(id) || id === getDicePalette().id) return;
  paletteId = id;
  setPref(PALETTE_KEY, id);
  redrawDiceArtwork();
  for (const fn of paletteListeners) fn(DICE_PALETTES[id]);
}

export function onDicePaletteChange(fn) {
  paletteListeners.add(fn);
  return () => paletteListeners.delete(fn);
}
/**
 * Numeral height on the face, per body. A d20 has twenty faces, so not much
 * room; a cube has six and can carry a bigger digit. The d4 prints three small
 * numerals per face, one in each corner.
 */

/** Deterministic per-cell jitter, so the same face always cuts the same paper. */
let numeralSheet = null;
let cardStock = null;
let artworkPending = null;

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Missing image: ${url}`));
    img.src = url;
  });
}

/**
 * Fetch numeral sheet and card stock. Once, in the background. If it fails the
 * die stays a piece of card without numbers; ugly, but it does not hold up the
 * throw. Every atlas already built is re-cut once the images arrive.
 */
export function loadDiceArtwork() {
  artworkPending ??= Promise.all([loadImage(NUMERALS_URL), loadImage(CARD_URL)])
    .then(([sheet, card]) => {
      numeralSheet = sheet;
      cardStock = card;
      redrawDiceArtwork();
    })
    .catch(() => {});
  return artworkPending;
}

/** Which sheet cell carries this number. 6 and 9 with the bar sit at the end. */
/** Skin images by URL: the image once it has arrived, `null` while it is on its way. */
const skinImages = new Map();
/** The fetch behind each of those, so a preload and a paint share one request. */
const skinImageLoads = new Map();

/** Fetch a skin image once; the atlases that sample it are re-cut when it lands. */
function loadSkinImage(url) {
  let pending = skinImageLoads.get(url);
  if (!pending) {
    skinImages.set(url, null);
    pending = loadImage(url)
      .then((img) => {
        skinImages.set(url, img);
        redrawDiceArtwork(url);
      })
      .catch(() => {});
    skinImageLoads.set(url, pending);
  }
  return pending;
}

/**
 * One of a skin's images (`tile`, `height`, `roughness`), or nothing yet. The
 * lab hands the same key in as a canvas under `<key>Image`, shown in place of
 * a URL. The first ask fetches it; every atlas is re-cut on arrival.
 */
function skinImage(palette, key) {
  const direct = palette[`${key}Image`];
  if (direct) return direct;
  const url = palette[key];
  if (!url) return null;
  loadSkinImage(url);
  return skinImages.get(url);
}

/**
 * The painter on the main thread: DOM canvases, the images loaded above.
 * Draws for the board, the tray and the roll-off, whose atlases follow the
 * player's palette and are re-cut as images land.
 */
const painter = createAtlasPainter({
  createCanvas: () => document.createElement('canvas'),
  numeralSheet: () => numeralSheet,
  cardStock: () => cardStock,
  skinImage,
});

/**
 * The atlas: one cell per face with card and numeral, the last cell blank card
 * for the chamfers and corners, plus the relief as a normal map and the
 * roughness. See `atlasPainter` for what goes into them.
 */
function buildTextures(sides, scale = 1, paletteOf = getDicePalette) {
  const paint = () => painter.paintAtlases(sides, scale, paletteOf());
  const first = paint();
  const map = new THREE.CanvasTexture(first.map);
  const normalMap = new THREE.CanvasTexture(first.normal);
  const roughnessMap = new THREE.CanvasTexture(first.roughness);
  const foilArtworkMap = new THREE.CanvasTexture(first.foilArt || first.map);
  foilArtworkMap.userData.foilMode = first.foilMode;
  foilArtworkMap.anisotropy = 4;
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 4;
  normalMap.anisotropy = 4;
  roughnessMap.anisotropy = 4;

  return {
    map,
    normalMap,
    roughnessMap,
    foilArtworkMap,
    redraw: () => {
      const next = paint();
      foilArtworkMap.image = next.foilArt || next.map;
      foilArtworkMap.userData.foilMode = next.foilMode;
      foilArtworkMap.needsUpdate = true;
      map.image = next.map;
      normalMap.image = next.normal;
      roughnessMap.image = next.roughness;
      map.needsUpdate = true;
      normalMap.needsUpdate = true;
      roughnessMap.needsUpdate = true;
    },
  };
}

/**
 * The body with chamfer: every face pulls in toward its centroid, the gaps
 * filled by narrow strips (edges) and small caps (corners). Un-indexed, so
 * `computeVertexNormals` yields exactly the hard facets a ground edge needs.
 */
function chamferedGeometry(sides) {
  const die = dieGeometry(sides);
  const positions = [];
  const uvs = [];
  const foilTangents = [];
  const foilUvs = [];
  const blank = cellCenter(sides, sides);

  const inset = [];
  const insetUv = [];
  const { cols, rows } = atlasLayout(sides);
  for (let f = 0; f < die.faces.length; f++) {
    const frame = faceFrame(die, f);
    const [cu, cv] = cellCenter(sides, f);
    inset.push(die.faces[f].map((vi) => {
      const v = die.vertices[vi];
      return vAdd(v, vScale(vSub(frame.center, v), CHAMFER));
    }));
    insetUv.push(die.faces[f].map((vi) => {
      const d = vSub(die.vertices[vi], frame.center);
      const s = FACE_FILL / frame.reach;
      return [cu + (vDot(d, frame.right) * s) / cols, cv + (vDot(d, frame.up) * s) / rows];
    }));
  }

  const pushTriangle = (a, b, c, ua, ub, uc, outward, right = null, face = false) => {
    // Secure the winding: the face must point outward, not into the body.
    const flip = vDot(vCross(vSub(b, a), vSub(c, a)), outward) < 0;
    const order = flip ? [a, c, b] : [a, b, c];
    const uvOrder = flip ? [ua, uc, ub] : [ua, ub, uc];
    const tangent = right || vNormalize(vCross(outward, Math.abs(outward[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0]));
    const up = vCross(outward, tangent);
    for (let i = 0; i < order.length; i++) {
      const p = order[i];
      positions.push(...p);
      foilTangents.push(...tangent);
      foilUvs.push(...(face ? [uvOrder[i][0] * cols % 1, uvOrder[i][1] * rows % 1] : [vDot(p, tangent) * 0.5 + 0.5, vDot(p, up) * 0.5 + 0.5]));
    }
    for (const uv of uvOrder) uvs.push(uv[0], uv[1]);
  };

  // 1. The faces themselves, as a fan around the first vertex.
  for (let f = 0; f < die.faces.length; f++) {
    for (let k = 1; k < inset[f].length - 1; k++) {
      pushTriangle(
        inset[f][0], inset[f][k], inset[f][k + 1],
        insetUv[f][0], insetUv[f][k], insetUv[f][k + 1],
        die.normals[f], faceFrame(die, f).right, true,
      );
    }
  }

  // 2. The edges: each pair of neighbouring faces leaves a strip free.
  const edges = new Map();
  for (let f = 0; f < die.faces.length; f++) {
    const face = die.faces[f];
    for (let i = 0; i < face.length; i++) {
      const a = face[i];
      const b = face[(i + 1) % face.length];
      const key = a < b ? `${a}:${b}` : `${b}:${a}`;
      const list = edges.get(key) ?? [];
      list.push({ face: f, a, b });
      edges.set(key, list);
    }
  }
  for (const pair of edges.values()) {
    if (pair.length !== 2) continue;
    const [e1, e2] = pair;
    const p1a = inset[e1.face][die.faces[e1.face].indexOf(e1.a)];
    const p1b = inset[e1.face][die.faces[e1.face].indexOf(e1.b)];
    const p2a = inset[e2.face][die.faces[e2.face].indexOf(e1.a)];
    const p2b = inset[e2.face][die.faces[e2.face].indexOf(e1.b)];
    const out = vNormalize(vAdd(die.normals[e1.face], die.normals[e2.face]));
    pushTriangle(p1a, p1b, p2b, blank, blank, blank, out);
    pushTriangle(p1a, p2b, p2a, blank, blank, blank, out);
  }

  // 3. The corners: three or more chamfers meet in a cap.
  for (let vi = 0; vi < die.vertices.length; vi++) {
    const around = [];
    for (let f = 0; f < die.faces.length; f++) {
      const at = die.faces[f].indexOf(vi);
      if (at >= 0) around.push(inset[f][at]);
    }
    if (around.length < 3) continue;
    const axis = vNormalize(die.vertices[vi]);
    const e1 = vNormalize(vCross(axis, Math.abs(axis[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0]));
    const e2 = vCross(axis, e1);
    around.sort(
      (p, q) => Math.atan2(vDot(p, e2), vDot(p, e1)) - Math.atan2(vDot(q, e2), vDot(q, e1)),
    );
    for (let k = 1; k < around.length - 1; k++) {
      pushTriangle(around[0], around[k], around[k + 1], blank, blank, blank, axis);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('foilTangent', new THREE.Float32BufferAttribute(foilTangents, 3));
  geometry.setAttribute('foilUv', new THREE.Float32BufferAttribute(foilUvs, 2));
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Card stock as a surface: matte, with a tooth, no gloss. A skin may override
 * any of these through its `material`; what it leaves out stays paper.
 */
export const PAPER_MATERIAL = {
  roughness: 0.92,
  metalness: 0,
  clearcoat: 0,
  clearcoatRoughness: 0,
  iridescence: 0,
  sheen: 0,
  envMapIntensity: 0.2,
};

/** Put a palette's surface onto a die material. */
function applyDieMaterial(material, palette = getDicePalette()) {
  Object.assign(material, PAPER_MATERIAL, palette.material);
  configureDieFoil(material, palette.foil, material.userData.diceSides);
  // With a roughness map the map is the roughness; the scalar only multiplies it.
  if (hasRoughnessMap(palette)) material.roughness = 1;
  // A clearcoat or sheen going from nothing to something is a new shader.
  material.needsUpdate = true;
}

/**
 * Every material cloned off a base, for as long as it lives, with the palette
 * getter it follows. A palette change has to reach the clones too; the shared
 * textures they follow on their own, the surface they do not.
 */
const clones = new Map();
/** Which palette getter a base material was built for. */
const materialPalette = new WeakMap();

/**
 * A die's own copy of a material (a hover glow or a loser's dimming must not
 * leak onto every other die), kept in step with the palette until disposed.
 */
export function cloneDieMaterial(source) {
  const material = source.clone();
  material.foilArtworkMap = source.foilArtworkMap;
  const paletteOf = materialPalette.get(source) ?? getDicePalette;
  materialPalette.set(material, paletteOf);
  clones.set(material, paletteOf);
  configureDieFoil(material, source.userData.diceFoil, source.userData.diceSides);
  material.addEventListener('dispose', () => clones.delete(material));
  return material;
}

/** Geometry, base material and the atlas redraw, per side count, atlas scale and palette. */
const assets = new Map();

function buildAssets(key, sides, scale, paletteOf) {
  const hit = assets.get(key);
  if (hit !== undefined) return hit;

  const textures = buildTextures(sides, scale, paletteOf);
  const material = new THREE.MeshPhysicalMaterial({
    map: textures.map,
    normalMap: textures.normalMap,
    roughnessMap: textures.roughnessMap,
  });
  material.foilArtworkMap = textures.foilArtworkMap;
  material.userData.diceSides = sides;
  materialPalette.set(material, paletteOf);
  applyDieMaterial(material, paletteOf());

  const built = { geometry: chamferedGeometry(sides), material, redraw: textures.redraw, paletteOf };
  assets.set(key, built);
  return built;
}

/**
 * `scale` multiplies the atlas texels: 1 for the tray's small dice, 2 where a
 * die fills the view (the initiative roll, the lab). Each scale is its own
 * material; the geometry is shared.
 */
export function dieAssets(sides, scale = 1) {
  return buildAssets(`${sides}@${scale}`, sides, scale, getDicePalette);
}

/**
 * The same body wearing one fixed skin, whatever the player's pick is. This
 * is how a shelf shows several skins side by side in one scene: each has its
 * own atlas and base material, keyed by the skin's id, and none of them
 * follows `previewDicePalette` or `setDicePalette`.
 */
export function skinDieAssets(sides, scale, skin) {
  return buildAssets(`${sides}@${scale}#${skin.id}`, sides, scale, () => skin);
}

/** Builds in flight through the worker, so two asks for one body share a cut. */
const pendingAssets = new Map();

function closeAtlasBitmaps(atlases) {
  for (const image of new Set([atlases.map, atlases.normal, atlases.roughness, atlases.foilArt])) image?.close?.();
}

/**
 * `skinDieAssets` with the cutting done off the main thread: the worker
 * fetches the skin's images itself, paints the three atlases on an
 * OffscreenCanvas and hands back bitmaps, so the main thread only uploads
 * textures. This is what a shelf of many skins uses; the synchronous path
 * would hitch the scroll for every body it cut. Falls back to the
 * synchronous cut where workers or OffscreenCanvas are missing. A later
 * re-cut of the same body (a palette image landing) goes through the
 * worker too.
 */
export function skinDieAssetsAsync(sides, scale, skin) {
  const key = `${sides}@${scale}#${skin.id}`;
  const hit = assets.get(key);
  if (hit !== undefined) return Promise.resolve(hit);
  const inFlight = pendingAssets.get(key);
  if (inFlight) return inFlight;

  const cut = () => cutAtlasesInWorker({ sides, scale, palette: skin, numeralsUrl: NUMERALS_URL, cardUrl: CARD_URL });
  const promise = cut()
    .then((bitmaps) => {
      if (bitmaps === null) return skinDieAssets(sides, scale, skin);
      const built = assets.get(key);
      if (built) { closeAtlasBitmaps(bitmaps); return built; }
      const texture = (bitmap, srgb) => {
        const t = new THREE.CanvasTexture(bitmap);
        // The worker flipped the bitmap on creation; WebGL ignores UNPACK_FLIP_Y for bitmaps.
        t.flipY = false;
        t.anisotropy = 4;
        if (srgb) t.colorSpace = THREE.SRGBColorSpace;
        return t;
      };
      const material = new THREE.MeshPhysicalMaterial({
        map: texture(bitmaps.map, true),
        normalMap: texture(bitmaps.normal, false),
        roughnessMap: texture(bitmaps.roughness, false),
      });
      material.foilArtworkMap = texture(bitmaps.foilArt || bitmaps.map, false);
      material.foilArtworkMap.userData.foilMode = bitmaps.foilMode;
      const paletteOf = () => skin;
      material.userData.diceSides = sides;
      materialPalette.set(material, paletteOf);
      applyDieMaterial(material, skin);
      const redraw = () => {
        cut().then((next) => {
          if (next === null) return;
          if (!assets.has(key)) { closeAtlasBitmaps(next); return; }
          closeAtlasBitmaps({ map: material.map.image, normal: material.normalMap.image, roughness: material.roughnessMap.image, foilArt: material.foilArtworkMap.image });
          material.foilArtworkMap.image = next.foilArt || next.map;
          material.foilArtworkMap.userData.foilMode = next.foilMode;
          material.foilArtworkMap.needsUpdate = true;
          material.map.image = next.map;
          material.normalMap.image = next.normal;
          material.roughnessMap.image = next.roughness;
          material.map.needsUpdate = true;
          material.normalMap.needsUpdate = true;
          material.roughnessMap.needsUpdate = true;
          applyDieMaterial(material, skin);
          for (const [clone, getPalette] of clones) {
            if (clone.foilArtworkMap === material.foilArtworkMap) applyDieMaterial(clone, getPalette());
          }
        });
      };
      const entry = { geometry: chamferedGeometry(sides), material, redraw, paletteOf };
      assets.set(key, entry);
      return entry;
    })
    .finally(() => pendingAssets.delete(key));
  pendingAssets.set(key, promise);
  return promise;
}

/**
 * Drop every atlas and material built for a skin. A shelf with hundreds of
 * skins cannot keep them all: a skin's atlases across the six bodies run to
 * tens of megabytes, so what has scrolled out of view is let go and cut
 * again if it comes back. Materials cloned off these must be disposed first.
 */
export function releaseSkinDieAssets(skinId) {
  const suffix = `#${skinId}`;
  for (const [key, built] of assets) {
    if (!key.endsWith(suffix)) continue;
    for (const t of [built.material.map, built.material.normalMap, built.material.roughnessMap, built.material.foilArtworkMap]) t?.image?.close?.();
    built.material.foilArtworkMap?.dispose();
    built.material.map?.dispose();
    built.material.normalMap?.dispose();
    built.material.roughnessMap?.dispose();
    built.material.dispose();
    built.geometry.dispose();
    assets.delete(key);
  }
}

/**
 * Once numeral sheet and card stock arrive, or the palette changes, every
 * built atlas is re-cut and every material takes on its palette's surface.
 * Given a skin image `url` that has just landed, only the atlases whose
 * palette samples that image are re-cut: every skin on a shelf brings three
 * images, and a full re-cut per arrival was quadratic in the shelf.
 */
export function redrawDiceArtwork(url = null) {
  const uses = (palette) => url === null || palette.tile === url || palette.height === url || palette.roughness === url;
  for (const built of assets.values()) {
    if (!uses(built.paletteOf())) continue;
    built.redraw();
    applyDieMaterial(built.material, built.paletteOf());
  }
  for (const [material, paletteOf] of clones) {
    if (uses(paletteOf())) applyDieMaterial(material, paletteOf());
  }
}
